"use client";

import { formatMoney, formatPercentile } from "@/lib/format";
import { MetricField, SliderField } from "@/components/dashboard/metrics-games/common";
import { TenKStartupSelector } from "@/components/dashboard/metrics-games/tenk-startup-selector";
import type { TenKGameInputs } from "@/components/dashboard/metrics-games/types";

type Props = {
  tenKInputs: TenKGameInputs;
  onUpdateTenKInput: (key: keyof TenKGameInputs, value: number) => void;
  searchInCard?: boolean;
  /** When provided, overrides the default "$10k MRR challenge" title (e.g. "{Niche} $10k MRR challenge"). */
  title?: string;
};

export function GameTenKMRR({ tenKInputs, onUpdateTenKInput, searchInCard, title }: Props) {
  const effectiveArpu = tenKInputs.arpuOverride > 0
    ? tenKInputs.arpuOverride
    : tenKInputs.nicheMedianArpu > 0
      ? tenKInputs.nicheMedianArpu
      : null;
  const userGrowthPerMonth = tenKInputs.startupAgeMonths > 0
    ? tenKInputs.currentMrr / tenKInputs.startupAgeMonths
    : 0;
  const blendedGrowth = userGrowthPerMonth * 0.65 + tenKInputs.marketMedianGrowthPerMonth * 0.35;
  const canComputeTenK = tenKInputs.currentMrr > 0 && tenKInputs.startupAgeMonths > 0 && blendedGrowth > 0;

  const tenKRemainingMonths = canComputeTenK
    ? Math.max(0, (10_000 - tenKInputs.currentMrr) / blendedGrowth)
    : null;
  const tenKTotalMonths = tenKRemainingMonths !== null
    ? tenKInputs.startupAgeMonths + tenKRemainingMonths
    : null;
  const tenKMonthsToMedian = tenKRemainingMonths !== null
    ? Math.max(0, (tenKInputs.nicheMedianMrr - tenKInputs.currentMrr) / blendedGrowth)
    : null;
  const tenKMonthsToTopQuartile = tenKRemainingMonths !== null
    ? Math.max(0, (tenKInputs.nicheTopQuartileMrr - tenKInputs.currentMrr) / blendedGrowth)
    : null;

  const tenKCustomersNeededMedian = effectiveArpu && effectiveArpu > 0
    ? Math.ceil(tenKInputs.nicheMedianMrr / effectiveArpu)
    : null;
  const tenKCustomersNeededTopQuartile = effectiveArpu && effectiveArpu > 0
    ? Math.ceil(tenKInputs.nicheTopQuartileMrr / effectiveArpu)
    : null;

  let tenKCurrentPercentile = 50;
  if (tenKInputs.nicheMedianMrr <= 0) {
    tenKCurrentPercentile = 50;
  } else if (tenKInputs.currentMrr < tenKInputs.nicheMedianMrr) {
    tenKCurrentPercentile = Math.max(
      5,
      Math.min(49, (tenKInputs.currentMrr / tenKInputs.nicheMedianMrr) * 50)
    );
  } else if (
    tenKInputs.nicheTopQuartileMrr > tenKInputs.nicheMedianMrr &&
    tenKInputs.currentMrr < tenKInputs.nicheTopQuartileMrr
  ) {
    tenKCurrentPercentile = 50 + (
      (tenKInputs.currentMrr - tenKInputs.nicheMedianMrr) /
      (tenKInputs.nicheTopQuartileMrr - tenKInputs.nicheMedianMrr)
    ) * 25;
  } else {
    tenKCurrentPercentile = Math.min(
      95,
      75 + (
        (tenKInputs.currentMrr - tenKInputs.nicheTopQuartileMrr) /
        Math.max(tenKInputs.nicheTopQuartileMrr, 1)
      ) * 15
    );
  }
  tenKCurrentPercentile = Number(tenKCurrentPercentile.toFixed(1));

  let tenKConfidence: "low" | "medium" | "high" = "medium";
  if (tenKRemainingMonths !== null && (tenKInputs.sampleSize < 8 || tenKRemainingMonths > 12)) {
    tenKConfidence = "low";
  }
  if (tenKRemainingMonths !== null && tenKInputs.sampleSize >= 15 && tenKRemainingMonths <= 6) {
    tenKConfidence = "high";
  }

  const tenKTargetDate = (() => {
    if (tenKRemainingMonths === null) return null;
    const date = new Date();
    date.setMonth(date.getMonth() + Math.round(tenKRemainingMonths));
    return date.toISOString().slice(0, 10);
  })();

  const displayTitle = title ?? "$10k MRR challenge";

  return (
    <article className="card metrics-game">
      <div className="metrics-game-head">
        <h2 className="card-title">{displayTitle}</h2>
      </div>
      <p className="card-desc">
        Simulate your path to $10k MRR using the same benchmark estimator logic used in NICHEMRR.
      </p>

      {searchInCard && (
        <div className="metrics-game-tenk-search">
          <TenKStartupSelector slot="both" />
        </div>
      )}

      <div className="metrics-inline-inputs metrics-inline-inputs--full-row metrics-tenk-layout">
        <div className="metrics-tenk-age">
          <SliderField
            label="Startup age"
            value={tenKInputs.startupAgeMonths}
            onChange={(v) => onUpdateTenKInput("startupAgeMonths", v)}
            min={1}
            max={60}
            step={1}
            formatValue={(v) =>
              v < 12 ? `${v} mo` : v % 12 === 0 ? `${v / 12} yr` : `${Math.floor(v / 12)} yr ${v % 12} mo`
            }
          />
          <div className="metrics-tenk-scale">
            <span style={{ left: "0%" }}>1mo</span>
            <span style={{ left: `${((6 - 1) / (60 - 1)) * 100}%` }}>6mo</span>
            <span style={{ left: `${((12 - 1) / (60 - 1)) * 100}%` }}>1yr</span>
            <span style={{ left: `${((24 - 1) / (60 - 1)) * 100}%` }}>2yr</span>
            <span style={{ left: `${((36 - 1) / (60 - 1)) * 100}%` }}>3yr</span>
            <span style={{ left: `${((48 - 1) / (60 - 1)) * 100}%` }}>4yr</span>
            <span style={{ left: "100%" }}>5yr</span>
          </div>
        </div>
        <div className="metrics-tenk-mrr">
          <SliderField
            label="Current MRR ($)"
            value={tenKInputs.currentMrr}
            onChange={(v) => onUpdateTenKInput("currentMrr", v)}
            min={0}
            max={10000}
            step={100}
            formatValue={(v) => formatMoney(v)}
          />
        </div>
        <div className="metrics-tenk-mmg">
          <SliderField
            label="MMG / mo ($)"
            value={tenKInputs.marketMedianGrowthPerMonth}
            onChange={(v) => onUpdateTenKInput("marketMedianGrowthPerMonth", v)}
            min={0}
            max={1000}
            step={10}
            formatValue={(v) => formatMoney(v)}
          />
        </div>
        <div className="metrics-tenk-niche-mrr">
          <SliderField
            label="Niche median MRR ($)"
            value={tenKInputs.nicheMedianMrr}
            onChange={(v) => onUpdateTenKInput("nicheMedianMrr", v)}
            min={0}
            max={15000}
            step={100}
            formatValue={(v) => formatMoney(v)}
          />
        </div>
        <div className="metrics-tenk-niche-quart">
          <SliderField
            label="Niche top quartile ($)"
            value={tenKInputs.nicheTopQuartileMrr}
            onChange={(v) => onUpdateTenKInput("nicheTopQuartileMrr", v)}
            min={0}
            max={20000}
            step={100}
            formatValue={(v) => formatMoney(v)}
          />
        </div>
        <div className="metrics-tenk-median-arpu">
          <SliderField
            label="Median ARPU ($)"
            value={tenKInputs.nicheMedianArpu}
            onChange={(v) => onUpdateTenKInput("nicheMedianArpu", v)}
            min={0}
            max={200}
            step={5}
            formatValue={(v) => formatMoney(v)}
          />
        </div>
        <div className="metrics-tenk-arpu-override">
          <SliderField
            label="ARPU override (optional)"
            value={tenKInputs.arpuOverride}
            onChange={(v) => onUpdateTenKInput("arpuOverride", v)}
            min={0}
            max={200}
            step={5}
            formatValue={(v) => (v > 0 ? formatMoney(v) : "—")}
          />
        </div>
        <div className="metrics-tenk-sample">
          <SliderField
            label="Sample size"
            value={tenKInputs.sampleSize}
            onChange={(v) => onUpdateTenKInput("sampleSize", v)}
            min={1}
            max={30}
            step={1}
          />
        </div>
      </div>

      <div className="score-breakdown-grid">
        <MetricField label="User growth / mo" value={formatMoney(userGrowthPerMonth)} />
        <MetricField label="Blended growth / mo" value={formatMoney(blendedGrowth)} />
        <MetricField
          label="Months to $10k"
          value={tenKRemainingMonths === null ? "-" : tenKRemainingMonths.toFixed(1)}
        />
        <MetricField
          label="Total months"
          value={tenKTotalMonths === null ? "-" : tenKTotalMonths.toFixed(1)}
        />
        <MetricField label="Target date" value={tenKTargetDate ?? "-"} />
        <MetricField label="Confidence" value={tenKConfidence} />
        <MetricField
          label="Current percentile"
          value={`${tenKCurrentPercentile.toFixed(1)} (${formatPercentile(tenKCurrentPercentile)})`}
        />
        <MetricField
          label="Months to niche median"
          value={tenKMonthsToMedian === null ? "-" : tenKMonthsToMedian.toFixed(1)}
        />
        <MetricField
          label="Months to top quartile"
          value={tenKMonthsToTopQuartile === null ? "-" : tenKMonthsToTopQuartile.toFixed(1)}
        />
        <MetricField label="Effective ARPU" value={effectiveArpu ? formatMoney(effectiveArpu) : "-"} />
        <MetricField
          label="Customers for median"
          value={tenKCustomersNeededMedian === null ? "-" : `${tenKCustomersNeededMedian}`}
        />
        <MetricField
          label="Customers for top quartile"
          value={tenKCustomersNeededTopQuartile === null ? "-" : `${tenKCustomersNeededTopQuartile}`}
        />
      </div>

      <p className="metrics-formula">
        <code>userGrowth = currentMrr / startupAgeMonths</code>
        <code>blendedGrowth = userGrowth*0.65 + marketMedianGrowthPerMonth*0.35</code>
        <code>monthsTo10k = max(0, (10000-currentMrr)/blendedGrowth)</code>
      </p>
      {tenKInputs.sampleSize < 8 && (
        <p className="card-desc">
          Small sample warning: benchmark quality is limited when sample size is below 8.
        </p>
      )}
    </article>
  );
}
